import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {isNotNil, not} from 'ramda';
import {isArray, isFalse} from 'ramda-adjunct';

import {PatchReceiveNoteBulkEditRequest} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  ReceiveNoteListBulkEditForm,
  ReceiveNoteListBulkEditFormSchema,
} from '../types/ReceiveNoteListBulkEditForm';

interface ReceiveNoteListBulkEditProps extends RequiredTestIdProps {
  rowId: string | string[];
  onSubmit: (requestBody: PatchReceiveNoteBulkEditRequest) => Promise<void>;
}

export function ReceiveNoteListBulkEdit(props: ReceiveNoteListBulkEditProps) {
  const handleDiscard = () => closeCurrentDialog();

  const handleSubmit: FormSubmitHandler<ReceiveNoteListBulkEditForm> = (formValues) => {
    return props.onSubmit({
      body: {
        receiveNoteIds: isArray(props.rowId) ? props.rowId : [props.rowId],
        invoiceNumber: formValues.invoiceNumber.invoiceNumber,
        invoiceIssueDate: formValues.invoiceIssueDate.invoiceIssueDate?.toISOString(),
        deliveryNoteNumber: formValues.deliveryNoteNumber.deliveryNoteNumber,
        deliveryNoteIssueDate:
          formValues.deliveryNoteIssueDate.deliveryNoteIssueDate?.toISOString(),
      },
    });
  };

  const defaultValues: ReceiveNoteListBulkEditForm = {
    invoiceNumber: {
      isInvoiceNumberEnabled: false,
    },
    invoiceIssueDate: {
      isInvoiceIssueDateEnabled: false,
    },
    deliveryNoteNumber: {
      isDeliveryNoteNumberEnabled: false,
    },
    deliveryNoteIssueDate: {
      isDeliveryNoteIssueDateEnabled: false,
    },
  };

  return (
    <Form<ReceiveNoteListBulkEditForm>
      experimentalZodSchema={ReceiveNoteListBulkEditFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const isInvoiceNumberEnabled = formApi.watch('invoiceNumber.isInvoiceNumberEnabled');
        const isInvoiceIssueDateEnabled = formApi.watch(
          'invoiceIssueDate.isInvoiceIssueDateEnabled'
        );
        const isDeliveryNoteNumberEnabled = formApi.watch(
          'deliveryNoteNumber.isDeliveryNoteNumberEnabled'
        );
        const isDeliveryNoteIssueDateEnabled = formApi.watch(
          'deliveryNoteIssueDate.isDeliveryNoteIssueDateEnabled'
        );

        const invoiceNumber = formApi.watch('invoiceNumber.invoiceNumber');
        const invoiceIssueDate = formApi.watch('invoiceIssueDate.invoiceIssueDate');
        const deliveryNoteNumber = formApi.watch('deliveryNoteNumber.deliveryNoteNumber');
        const deliveryNoteIssueDate = formApi.watch('deliveryNoteIssueDate.deliveryNoteIssueDate');

        const hasChanges =
          isNotNil(invoiceNumber) ||
          isNotNil(invoiceIssueDate) ||
          isNotNil(deliveryNoteNumber) ||
          isNotNil(deliveryNoteIssueDate);

        return (
          <VStack spacing={4}>
            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="deliveryNoteNumber.isDeliveryNoteNumberEnabled"
                onChange={(value) =>
                  isFalse(value) && formApi.setValue('deliveryNoteNumber.deliveryNoteNumber', null)
                }
                data-testid={suffixTestId('inputs.isDeliveryNoteNumberEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isDeliveryNoteNumberEnabled)}
                  control={control}
                  type="text"
                  name="deliveryNoteNumber.deliveryNoteNumber"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
                  data-testid={suffixTestId('inputs.deliveryNoteNumber', props)}
                />
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="deliveryNoteIssueDate.isDeliveryNoteIssueDateEnabled"
                onChange={(value) =>
                  isFalse(value) &&
                  formApi.setValue('deliveryNoteIssueDate.deliveryNoteIssueDate', null)
                }
                data-testid={suffixTestId('inputs.isDeliveryNoteIssueDateEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isDeliveryNoteIssueDateEnabled)}
                  control={control}
                  type="date"
                  name="deliveryNoteIssueDate.deliveryNoteIssueDate"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteIssueDate')}
                  data-testid={suffixTestId('inputs.deliveryNoteIssueDate', props)}
                />
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="invoiceNumber.isInvoiceNumberEnabled"
                onChange={(value) =>
                  isFalse(value) && formApi.setValue('invoiceNumber.invoiceNumber', null)
                }
                data-testid={suffixTestId('inputs.isInvoiceNumberEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isInvoiceNumberEnabled)}
                  control={control}
                  type="text"
                  name="invoiceNumber.invoiceNumber"
                  label={i18n.t('entity.warehouse.labels.invoiceNumber')}
                  data-testid={suffixTestId('inputs.invoiceNumber', props)}
                />
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="invoiceIssueDate.isInvoiceIssueDateEnabled"
                onChange={(value) =>
                  isFalse(value) && formApi.setValue('invoiceIssueDate.invoiceIssueDate', null)
                }
                data-testid={suffixTestId('inputs.isInvoiceIssueDateEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isInvoiceIssueDateEnabled)}
                  control={control}
                  type="date"
                  name="invoiceIssueDate.invoiceIssueDate"
                  label={i18n.t('entity.warehouse.labels.invoiceIssueDate')}
                  data-testid={suffixTestId('inputs.invoiceIssueDate', props)}
                />
              </Box>
            </HStack>

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={handleDiscard}
                data-testid={suffixTestId('actions.discard', props)}
              />
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('general.actions.apply')}
                isDisabled={not(hasChanges)}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
